import { useCallback, useContext } from "react";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import {
    GiftingCartApiResponse,
    CombinedCartInStockItemRender,
    CartItem,
    IMStructureItem,
} from "@swiggy-private/cart-types";
import { Item } from "@swiggy-private/dls-web-recipe-common-web/dist/components/ReviewItemListCard";
import { SCREEN_NAME } from "@cart-client/analytics/constants";
import { ANALYTICS_EVENT_NAMES } from "./constants";

interface UseGetReviewItemListAnalyticsProps {
    reviewedCart?: GiftingCartApiResponse | null;
}

export const useGetReviewItemListAnalytics = ({ reviewedCart }: UseGetReviewItemListAnalyticsProps) => {
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const generateAnalyticsPayload = useCallback(
        ({ item }: { item: Item }) => {
            return JSON.stringify({
                restaurantId: reviewedCart?.restaurant_details?.id,
                originalCartId: reviewedCart?.originalCartId,
                cartId: reviewedCart?.cartId,
                itemId: item?.id,
                businessLine: item?.subType,
                itemQuantity: item?.quantity,
                basePrice: item?.basePrice,
                finalPrice: item?.finalPrice,
            });
        },
        [reviewedCart?.cartId, reviewedCart?.originalCartId, reviewedCart?.restaurant_details?.id],
    );

    const generateItemListAnalyticsPayload = useCallback((items: (CartItem & IMStructureItem)[]) => {
        return items
            ?.map((item: CartItem & IMStructureItem) => {
                return (item as CartItem)?.menu_item_id || (item as IMStructureItem)?.itemId;
            })
            ?.join(",");
    }, []);

    const generateCartAnalyticsPayload = useCallback(
        (cart: CombinedCartInStockItemRender) => {
            return JSON.stringify({
                businessLine: cart?.businessLine,
                slaTime: cart?.slaTime,
                slaTimeString: cart?.slaTimeString,
                isServiceable: cart?.isServiceable,
                items: generateItemListAnalyticsPayload(cart?.items as (CartItem & IMStructureItem)[]),
            });
        },
        [generateItemListAnalyticsPayload],
    );

    const impressionCartEvent = useCallback(
        (cart: CombinedCartInStockItemRender) => {
            return impressionEvent({
                category: SCREEN_NAME.GIFTING_CHECKOUT,
                action: ANALYTICS_EVENT_NAMES.IMPRESSION.CART,
                extraParams: generateCartAnalyticsPayload(cart),
            });
        },
        [impressionEvent, generateCartAnalyticsPayload],
    );

    const impressionItemEvent = useCallback(
        ({ item }: { item: Item }) => {
            return impressionEvent({
                category: SCREEN_NAME.GIFTING_CHECKOUT,
                action: ANALYTICS_EVENT_NAMES.IMPRESSION.ITEM,
                extraParams: generateAnalyticsPayload({ item }),
            });
        },
        [generateAnalyticsPayload, impressionEvent],
    );

    const clickQuantityIncEvent = useCallback(
        ({ item }: { item: Item }) => {
            return clickEvent({
                category: SCREEN_NAME.GIFTING_CHECKOUT,
                action: ANALYTICS_EVENT_NAMES.CLICK.QUANTITY_INC,
                extraParams: generateAnalyticsPayload({ item }),
            });
        },
        [clickEvent, generateAnalyticsPayload],
    );

    const clickQuantityDecEvent = useCallback(
        ({ item }: { item: Item }) => {
            return clickEvent({
                category: SCREEN_NAME.GIFTING_CHECKOUT,
                action: ANALYTICS_EVENT_NAMES.CLICK.QUANTITY_DEC,
                extraParams: generateAnalyticsPayload({ item }),
            });
        },
        [clickEvent, generateAnalyticsPayload],
    );

    const clickAddToCartEvent = useCallback(
        ({ item }: { item: Item }) => {
            return clickEvent({
                category: SCREEN_NAME.GIFTING_CHECKOUT,
                action: ANALYTICS_EVENT_NAMES.CLICK.ADD_TO_CART,
                extraParams: generateAnalyticsPayload({ item }),
            });
        },
        [clickEvent, generateAnalyticsPayload],
    );

    const clickRemoveFromCartEvent = useCallback(
        ({ item }: { item: Item }) => {
            return clickEvent({
                category: SCREEN_NAME.GIFTING_CHECKOUT,
                action: ANALYTICS_EVENT_NAMES.CLICK.REMOVE_FROM_CART,
                extraParams: generateAnalyticsPayload({ item }),
            });
        },
        [clickEvent, generateAnalyticsPayload],
    );

    return {
        impressionCartEvent,
        impressionItemEvent,
        clickQuantityIncEvent,
        clickQuantityDecEvent,
        clickAddToCartEvent,
        clickRemoveFromCartEvent,
    };
};
